import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { ApiResponse } from '../_model/api-response';
import { MlAdminOperationsSnapshot } from '../_model/machine-learning';
import { MachineLearningAdminService } from './machine-learning-admin.service';

interface OverviewResult<T> {
  data: T | null;
  error: string | null;
}

@Injectable({ providedIn: 'root' })
export class MachineLearningOperationsOverviewService {
  private snapshot$?: Observable<MlAdminOperationsSnapshot>;
  private snapshotCreatedAt = 0;
  private readonly cacheTtlMilliseconds = 30_000;
  private readonly refreshSubject = new BehaviorSubject<void>(undefined);

  constructor(private mlAdmin: MachineLearningAdminService) {}

  getSnapshot(forceRefresh = false): Observable<MlAdminOperationsSnapshot> {
    const cacheExpired = Date.now() - this.snapshotCreatedAt >= this.cacheTtlMilliseconds;

    if (forceRefresh || cacheExpired || !this.snapshot$) {
      this.snapshotCreatedAt = Date.now();
      this.snapshot$ = forkJoin({
        lifecycle: this.safeRead('Lifecycle overview', this.mlAdmin.getLoginRiskLifecycleOverview()),
        withdrawalRisk: this.safeRead('Withdrawal-risk summary', this.mlAdmin.getWithdrawalRiskSummary()),
        withdrawalAlerts: this.safeRead('Withdrawal-risk alerts', this.mlAdmin.getWithdrawalRiskAlerts()),
        depositRisk: this.safeRead('Deposit-risk summary', this.mlAdmin.getDepositRiskSummary()),
        depositAlerts: this.safeRead('Deposit-risk alerts', this.mlAdmin.getDepositRiskAlerts()),
        liquidity: this.safeRead('Liquidity summary', this.mlAdmin.getLiquiditySummary()),
        liquidityAlerts: this.safeRead('Liquidity alerts', this.mlAdmin.getLiquidityAlerts()),
        deploymentHealth: this.safeRead('Deployment health', this.mlAdmin.getLoginRiskDeploymentHealth()),
        deploymentAlerts: this.safeRead('Deployment alerts', this.mlAdmin.getLoginRiskDeploymentAlerts()),
        candidateGovernance: this.safeRead('Candidate governance', this.mlAdmin.getLoginRiskCandidateSummary()),
        automaticRollback: this.safeRead('Automatic rollback status', this.mlAdmin.getLoginRiskAutomaticRollbackStatus()),
        matching: this.safeRead('Matching intelligence', this.mlAdmin.getMatchingIntelligenceSummary()),
        settlementReliability: this.safeRead('Settlement reliability', this.mlAdmin.getSettlementReliabilitySummary()),
        gatewayReliability: this.safeRead('Gateway reliability', this.mlAdmin.getGatewayReliabilitySummary()),
        operationalAlerts: this.safeRead('Operational reliability alerts', this.mlAdmin.getOperationalReliabilityAlerts())
      }).pipe(
        map(result => {
          const partialErrors = Object.values(result)
            .map(item => item.error)
            .filter((value): value is string => !!value);

          return {
            lifecycle: result.lifecycle.data,
            withdrawalRisk: result.withdrawalRisk.data,
            withdrawalAlerts: result.withdrawalAlerts.data ?? [],
            depositRisk: result.depositRisk.data,
            depositAlerts: result.depositAlerts.data ?? [],
            liquidity: result.liquidity.data,
            liquidityAlerts: result.liquidityAlerts.data ?? [],
            deploymentHealth: result.deploymentHealth.data,
            deploymentAlerts: result.deploymentAlerts.data ?? [],
            candidateGovernance: result.candidateGovernance.data,
            automaticRollback: result.automaticRollback.data,
            matching: result.matching.data,
            settlementReliability: result.settlementReliability.data,
            gatewayReliability: result.gatewayReliability.data,
            operationalAlerts: result.operationalAlerts.data ?? [],
            partialErrors,
            retrievedAt: new Date().toISOString()
          } as MlAdminOperationsSnapshot;
        }),
        shareReplay(1)
      );
    }

    return this.snapshot$;
  }

  watchSnapshot(): Observable<MlAdminOperationsSnapshot> {
    return this.refreshSubject.pipe(
      switchMap(() => this.getSnapshot()),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  invalidate(): void {
    this.snapshot$ = undefined;
    this.snapshotCreatedAt = 0;
    this.refreshSubject.next();
  }

  clearCache(): void {
    this.snapshot$ = undefined;
    this.snapshotCreatedAt = 0;
  }

  private safeRead<T>(label: string, source: Observable<ApiResponse<T>>): Observable<OverviewResult<T>> {
    return source.pipe(
      map(response => ({
        data: response.data ?? null,
        error: response.success
          ? null
          : `${label}: ${response.error?.details || response.message || 'unavailable'}`
      })),
      catchError(error => of({
        data: null,
        error: `${label}: ${error?.error?.error?.details || error?.error?.message || error?.message || 'unavailable'}`
      }))
    );
  }
}
